import React, { useCallback } from "react";
import styled from "styled-components";
import { Search, Mic } from "react-feather";
import { rawColors } from "../design-system/tokens/colors";
import { spacing } from "../design-system/tokens/spacing";
import { borderRadius } from "../design-system/tokens/borders";
import { useCommandRegistry } from "../contexts/CommandRegistryContext";
import { isMacPlatform } from "../services/commands/commands.types";
import { ShortcutKeys } from "./atoms/ShortcutKeys";
import { mediaQueries } from "../design-system/tokens/breakpoints";
import { fontSizes, typographyMixin } from "../design-system/tokens/typography";

const SEARCH_COPY = {
  placeholder: "Search stories, data and commands",
  label: "Global search",
  voice: "Voice",
} as const;

/** F20 bar: max 520 wide, 36px tall, pill, grey-100 fill, padding 0 6 0 14, gap 10. */
const SearchBarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2.5]};
  height: ${spacing[9]};
  background: ${rawColors.grey[100]};
  border: 1px solid transparent;
  border-radius: ${borderRadius.full};
  padding: 0 ${spacing[1.5]} 0 0.875rem /* GAP: 14px has no spacing token */;
  cursor: text;
  width: 100%;
  max-width: 32.5rem;
  box-sizing: border-box;
  position: relative;
  flex-shrink: 1;

  &:focus-within {
    border-color: ${rawColors.primary[400]};
  }

  /* Phone: collapses to the search icon plus the voice button; the field stays tappable and focusable, and opens the palette. */
  ${mediaQueries.down.xs} {
    width: auto;
    flex: none;
    gap: ${spacing[1.5]};
    padding: 0 ${spacing[1]} 0 ${spacing[2.5]};
    cursor: pointer;
  }
`;

/** Shortcut hint (a label, not an option): wide screens only. */
const WideOnly = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2.5]};
  ${mediaQueries.down.xs} {
    display: none;
  }
`;

const SearchIconWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: ${rawColors.text.lowEmphasis};
`;

const SearchInput = styled.input`
  flex: 1 0 0;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  ${typographyMixin("body", 4)}
  font-size: ${fontSizes[13]};
  color: ${rawColors.text.highEmphasis};
  cursor: text;

  &::placeholder {
    color: ${rawColors.text.lowEmphasis};
  }

  /* Phone: an invisible field over the icon keeps tap and keyboard focus working. */
  ${mediaQueries.down.xs} {
    position: absolute;
    inset: 0;
    width: 100%;
    opacity: 0;
    cursor: pointer;
  }
`;

/** 28px white circle with a brand-primary mic. */
const VoiceButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${spacing[7]};
  height: ${spacing[7]};
  padding: 0;
  border: none;
  border-radius: ${borderRadius.full};
  background: ${rawColors.structural.white};
  color: ${rawColors.brand.primary};
  cursor: pointer;

  &:hover { background: ${rawColors.primary[100]}; }
`;

const SearchBar: React.FC = React.memo(() => {
  const { ui } = useCommandRegistry();
  const mac = isMacPlatform();
  const open = useCallback(() => ui.setPaletteOpen(true), [ui]);

  // The field is the palette's front door: focusing or clicking it opens the palette (the one ⌘K binding lives in the command registry).
  return (
    <SearchBarContainer onClick={open}>
      <SearchIconWrap aria-hidden>
        <Search size={18} />
      </SearchIconWrap>
      <SearchInput
        type="search"
        placeholder={SEARCH_COPY.placeholder}
        value=""
        readOnly
        onFocus={(event) => {
          open();
          event.currentTarget.blur();
        }}
        aria-label={SEARCH_COPY.label}
      />
      <WideOnly>
        <ShortcutKeys chords={[{ key: "k", mod: true }]} mac={mac} joined />
      </WideOnly>
      {/* GAP: the palette has no voice-mode switch yet, so this opens the palette like the field does. */}
      <VoiceButton
        type="button"
        aria-label={SEARCH_COPY.voice}
        onClick={(event) => {
          event.stopPropagation();
          open();
        }}
      >
        <Mic size={14} />
      </VoiceButton>
    </SearchBarContainer>
  );
});

SearchBar.displayName = "SearchBar";
export default SearchBar;
